// The Mantine theme, built from the generated tokens so the console reads as
// MotionSafe: warm neutrals, one accent, flat hairline surfaces, the system
// stack, two-move motion. Component defaults below are the design rules
// applied once, so screens inherit them.

import { Button, createTheme, Tabs, Title, Tooltip, type CSSVariablesResolver, type MantineColorsTuple } from '@mantine/core';

import { fontFamily, palette, radius, scales, spacing, type } from './theme.gen';

const tuple = (s: readonly string[]) => s as unknown as MantineColorsTuple;

// Tokens.motion.durationMs (DESIGN.web.md 7.4): a control's own change, a
// local move, a surface arriving. One easing for all of them.
const motion = { micro: 120, local: 200, surface: 280 } as const;
/** The motion durations in milliseconds, for a component that sets its own transition. */
export const motionMs = motion;
export const EASE_OUT = 'cubic-bezier(0.22, 1, 0.36, 1)';

// A menu slides a few pixels out of its anchor while it fades (the Continuity
// Rule): down from a trigger above it by default, up from one below it.
const fromAnchor = (dy: number) => ({
  in: { opacity: 1, transform: 'translateY(0)' },
  out: { opacity: 0, transform: `translateY(${dy}px)` },
  transitionProperty: 'transform, opacity',
});
export const menuFromBelow = { transition: fromAnchor(4), duration: motion.local, timingFunction: EASE_OUT } as const;
export const menuFromAbove = { transition: fromAnchor(-4), duration: motion.local, timingFunction: EASE_OUT } as const;

// Sheet and dialog titles take the title face (18/24, 600).
const SURFACE_TITLE = { fontSize: type.title.size, lineHeight: type.title.lineHeight, fontWeight: type.title.weight } as const;

// The display and headline faces carry their tracking; the smaller faces
// have none. Keyed by heading order.
const HEADING_TRACKING: Partial<Record<number, string>> = { 1: type.display.tracking, 2: type.headline.tracking };

// Icons by role, so a glyph is the same size wherever it plays the same
// part: one that leads or ends a button, a section link, or an alert is
// 18/1.5; one inside an input, a menu item, a link, or a compact button is
// 16/1.75.
export const ICON = { size: 18, stroke: 1.5 } as const;
export const ICON_INLINE = { size: 16, stroke: 1.75 } as const;

// The eyebrow face (14/18, 500, uppercase, 0.6px): the categorical label
// above a group of facts, such as a drawer section or the wall's report.
export const EYEBROW = { size: 'sm', fw: type.eyebrow.weight, c: 'dimmed', tt: 'uppercase', lts: type.eyebrow.tracking } as const;

declare module '@mantine/core' {
  interface MantineThemeOther {
    motion: { micro: number; local: number; surface: number };
  }
}

export const theme = createTheme({
  // The web face (DESIGN.md 7.2): Plex Sans over the shared system stack,
  // which stands in until the font file arrives and on any platform without it.
  fontFamily: `'IBM Plex Sans Variable', ${fontFamily}`,
  fontFamilyMonospace: `'IBM Plex Mono', ui-monospace, "SF Mono", Menlo, Consolas, monospace`,
  primaryColor: 'instrument',
  primaryShade: { light: 6, dark: 4 },
  autoContrast: true,
  colors: {
    instrument: tuple(scales.instrument),
    walnut: tuple(scales.walnut),
    success: tuple(scales.success),
    warning: tuple(scales.warning),
    danger: tuple(scales.danger),
    // Mantine's own neutral, aliased to the warm scale so a `gray` badge or
    // border never reads cool. The Warmth Carries Rule, applied at the token.
    gray: tuple(scales.walnut),
    // Mantine's dark-scheme neutral, by the role each shade plays there: text,
    // secondary text, placeholder, hairline and hover, surface, page. Switch
    // tracks, badge fills, and row hovers in dark read from it.
    dark: tuple([
      palette['warm-bone'], palette['warm-bone'], palette['taupe-on-walnut'], palette['warm-taupe'],
      palette['inner-walnut'], palette['inner-walnut'], palette['lifted-walnut'],
      palette['hearth-brown'], palette['hearth-brown'], palette['hearth-brown'],
    ]),
  },
  black: palette['espresso-ink'],
  white: palette['page-white'],
  defaultRadius: 'md',
  radius: { xs: '6px', sm: radius.sm, md: radius.md, lg: radius.lg, xl: '28px' },
  spacing: { xs: spacing.xs, sm: spacing.sm, md: spacing.md, lg: spacing.lg, xl: spacing.xxl },
  fontSizes: { xs: type.caption.size, sm: type.label.size, md: type.body.size, lg: type.title.size, xl: type.headline.size },
  lineHeights: { xs: '1.33', sm: '1.3', md: '1.5', lg: '1.35', xl: '1.27' },
  headings: {
    fontFamily: `'IBM Plex Sans Variable', ${fontFamily}`,
    fontWeight: '600',
    // A heading that wraps splits into lines of even length.
    textWrap: 'balance',
    sizes: {
      h1: { fontSize: type.display.size, lineHeight: '1.2', fontWeight: '700' },
      h2: { fontSize: type.headline.size, lineHeight: '1.27' },
      h3: { fontSize: type.title.size, lineHeight: '1.33' },
      h4: { fontSize: type.body.size, lineHeight: '1.5' },
    },
  },
  shadows: { xs: 'none', sm: 'none', md: '0 3px 5px rgb(26 20 8 / 0.25)', lg: '0 3px 5px rgb(26 20 8 / 0.25)', xl: '0 3px 5px rgb(26 20 8 / 0.25)' },
  cursorType: 'pointer',
  focusRing: 'auto',
  components: {
    Paper: { defaultProps: { withBorder: true, shadow: 'none', radius: 'md' } },
    Card: { defaultProps: { withBorder: true, shadow: 'none', radius: 'md' } },
    // Every button is 44px tall, whatever its size: the console is used on
    // tablets in a browser too (7.5). Pages use two sizes: md, the default,
    // for every page, form, and dialog action; compact-sm, whose type and
    // padding are a step smaller, for a quiet action inside a card or a row.
    // 44px tall by default: page and dialog actions, used on tablets in a
    // browser too. The `sm` button is the toolbar's, at input height beside
    // a search field; compact sizes keep their own height as well.
    Button: Button.extend({ defaultProps: { radius: '10px', size: 'md' }, styles: (_theme, props) => ({ root: { fontWeight: 500, ...(props.size === undefined || props.size === 'md' ? { height: 44 } : {}) } }) }),
    Title: Title.extend({ styles: (_theme, { order }) => ({ root: { letterSpacing: HEADING_TRACKING[order ?? 1] } }) }),
    ActionIcon: { defaultProps: { variant: 'subtle' } },
    // Material 3's primary tabs, as the tablet's Android knows them: a 48px
    // row over a hairline, the chosen tab's label in Instrument Blue with a
    // 3px bar under the label alone, rounded on top, and an 8% state layer on
    // hover (styles/app.css, .m3-tab). Library styles off, so nothing of
    // Mantine's own variant shows through.
    Tabs: Tabs.extend({ defaultProps: { unstyled: true }, classNames: { list: 'm3-tabs-list', tab: 'm3-tab', tabLabel: 'm3-tab-label', tabSection: 'm3-tab-section' } }),
    // The two floating surfaces sit on the overlay, never a shadow (7.3). A
    // dialog keeps the tablet's geometry: its title and body inset by xl.
    Modal: { defaultProps: { radius: 'lg', padding: spacing.xl, shadow: 'none', overlayProps: { backgroundOpacity: 0.45 }, transitionProps: { transition: 'fade', duration: motion.local, timingFunction: EASE_OUT } }, styles: { title: SURFACE_TITLE, content: { display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }, header: { flex: 'none', borderBottom: '1px solid var(--mantine-color-default-border)' }, body: { overflowY: 'auto', minHeight: 0, paddingTop: 'var(--mb-padding, var(--mantine-spacing-md))' } } },
    Drawer: { defaultProps: { position: 'right', size: 'md', shadow: 'none', overlayProps: { backgroundOpacity: 0.45 }, transitionProps: { duration: motion.surface, timingFunction: EASE_OUT } }, styles: { title: SURFACE_TITLE } },
    // A menu is a short list read at a glance: body-small type, an icon in
    // the secondary ink leading each line, room around the groups, and red
    // for the one thing that cannot be taken back.
    Menu: {
      defaultProps: { shadow: 'none', radius: 'md', withOverlay: true, overlayProps: { backgroundOpacity: 0.2 }, transitionProps: menuFromAbove },
      styles: {
        dropdown: { padding: 6, minWidth: 208 },
        item: { fontSize: 'var(--mantine-font-size-sm)', padding: '8px 10px', borderRadius: 8 },
        itemSection: { marginInlineEnd: 10 },
        divider: { marginBlock: 6, marginInline: 4 },
        label: { fontSize: 'var(--mantine-font-size-xs)', padding: '6px 10px 4px' },
      },
    },
    // The Tabular Number Rule on every table; a hovered row takes the
    // recessed surface, one tone off the table (7.5).
    Table: { defaultProps: { verticalSpacing: 'sm', horizontalSpacing: 'lg', highlightOnHover: true, highlightOnHoverColor: 'var(--mantine-color-default-hover)', tabularNums: true } },
    TextInput: { defaultProps: { radius: 'md' } },
    NumberInput: { defaultProps: { radius: 'md' } },
    Select: { defaultProps: { radius: 'md' } },
    // A status word is never cut short: the badge keeps its whole word in a
    // narrow column or a crowded row instead of ellipsizing to its dot.
    Badge: { defaultProps: { variant: 'light', radius: 'sm', size: 'lg' }, styles: { root: { textTransform: 'none', fontWeight: 500, letterSpacing: 0, flexShrink: 0 }, label: { overflow: 'visible' } } },
    // Sidebar rows: one height, the icon in a fixed column in the secondary
    // ink, the label at the secondary size; the current row's label a step
    // bolder and its icon in the text color.
    NavLink: {
      defaultProps: { variant: 'light' },
      styles: {
        root: { borderRadius: 'var(--mantine-radius-sm)', minHeight: 36, paddingTop: 6, paddingBottom: 6, paddingLeft: 10, paddingRight: 10 },
        label: { fontSize: 'var(--mantine-font-size-sm)', fontWeight: 500, lineHeight: 1.3 },
        section: { color: 'var(--mantine-color-dimmed)' },
      },
    },
    // A tooltip is a moment's note on what is under the pointer, read and
    // gone: never a flash. On the light page it is the text ink as a surface,
    // as the toast is; on the dark page a walnut sheet a step above the rows
    // it sits over, held off them by a warm hairline, not an inverted slab of
    // cream. Room around the words, the small radius, and it fades in place.
    Tooltip: Tooltip.extend({
      defaultProps: { withArrow: false, radius: 'sm', transitionProps: { transition: 'fade', duration: motion.micro, timingFunction: EASE_OUT } },
      vars: () => ({ tooltip: { '--tooltip-bg': 'var(--ms-tooltip-bg)', '--tooltip-color': 'var(--ms-tooltip-color)' } }),
      styles: { tooltip: { padding: '8px 12px', fontSize: 'var(--mantine-font-size-sm)', lineHeight: 1.45, border: '1px solid var(--ms-tooltip-border)' } },
    }),
    // An empty table says what fills it: a small tinted mark, the sentence,
    // the action. Sized to the table it sits in, not to a landing page.
    EmptyState: {
      defaultProps: { size: 'sm', variant: 'light' },
      vars: () => ({ root: { '--empty-state-indicator-size': '22px' } }),
      styles: {
        indicator: { borderRadius: 'var(--mantine-radius-md)' },
        title: { color: 'var(--mantine-color-text)', lineHeight: 'var(--mantine-line-height-md)' },
        description: { lineHeight: 'var(--mantine-line-height-sm)', maxWidth: 480 },
      },
    },
  },
  other: { motion },
});

// Instrument Blue Tint and the state tints (the frontmatter's *-tint tokens)
// are what a light-variant surface is: the selected row, the current section,
// an alert's slab, the empty state's mark. Mantine's own light variant is the
// scale's second shade, several times stronger. Text on the blue tint takes
// the scale's darker step so it holds 4.5:1 on the tint.
const instrumentLight = {
  light: { bg: palette['instrument-blue-tint'], hover: `${palette['instrument-blue']}29`, color: scales.instrument[7] },
  // The same 10% tint of the dark theme's blue.
  dark: { bg: palette['instrument-blue-dark-tint'], hover: `${palette['instrument-blue-dark']}29`, color: palette['instrument-blue-dark'] },
};
const lightVariant = (scheme: 'light' | 'dark') => {
  const v = instrumentLight[scheme];
  return {
    '--mantine-color-instrument-light': v.bg,
    '--mantine-color-instrument-light-hover': v.hover,
    '--mantine-color-instrument-light-color': v.color,
    '--mantine-primary-color-light': v.bg,
    '--mantine-primary-color-light-hover': v.hover,
    '--mantine-primary-color-light-color': v.color,
  };
};

/** The motion tokens, and page and text colors per scheme: the Notebook palette, never Mantine's cool grays. */
export const cssVariablesResolver: CSSVariablesResolver = (t) => ({
  variables: {
    '--motion-micro': `${t.other.motion.micro}ms`,
    '--motion-local': `${t.other.motion.local}ms`,
    '--motion-surface': `${t.other.motion.surface}ms`,
    '--ease-out': EASE_OUT,
  },
  light: {
    '--mantine-color-body': palette['notebook-cream'],
    '--mantine-color-text': palette['espresso-ink'],
    '--mantine-color-dimmed': palette['warm-taupe'],
    '--mantine-color-default': palette['page-white'],
    '--mantine-color-default-border': palette['notebook-edge'],
    '--mantine-color-default-hover': palette['notebook-margin'],
    '--mantine-color-placeholder': palette['warm-taupe'],
    '--mantine-color-anchor': palette['instrument-blue'],
    // A button that cannot be pressed: the border color's fill and the
    // secondary ink (DESIGN.md, Buttons, Disabled), so its word stays readable.
    '--mantine-color-disabled': palette['notebook-edge'],
    '--mantine-color-disabled-border': palette['notebook-edge'],
    '--mantine-color-disabled-color': palette['warm-taupe'],
    // An input's fill: Page White on the cream page, as the app draws it.
    '--ms-input-bg': palette['page-white'],
    // A tooltip: the text ink as its surface, the cream as its words.
    '--ms-tooltip-bg': palette['espresso-ink'],
    '--ms-tooltip-color': palette['notebook-cream'],
    '--ms-tooltip-border': palette['espresso-ink'],
    // The logo's own two colors (adroid/assets/logo/logo-light-mode.svg),
    // for the wordmark only: brand art, not palette roles.
    '--ms-brand-ink': '#000000',
    '--ms-brand-blue': '#067EA4',
    // A refusal under an input is Vermillion, the same red as every other
    // refusal sentence in the console.
    '--mantine-color-error': 'var(--mantine-color-danger-text)',
    ...lightVariant('light'),
    '--mantine-color-warning-light': palette['workshop-amber-tint'],
    '--mantine-color-danger-light': palette['vermillion-tint'],
    '--mantine-color-success-light': palette['field-green-tint'],
    // Text on a light-variant state surface (a badge) takes the next darker
    // step of its scale, so the word holds near 4.5:1 on the badge's own
    // tint while still reading as its color: green, amber, red.
    '--mantine-color-success-light-color': scales.success[7],
    '--mantine-color-warning-light-color': scales.warning[7],
    '--mantine-color-danger-light-color': scales.danger[7],
  },
  dark: {
    // The logo's dark colors (adroid/assets/logo/logo-dark-mode.svg).
    '--ms-brand-ink': '#FEFEFE',
    '--ms-brand-blue': '#03BCF2',
    '--mantine-color-body': palette['hearth-brown'],
    '--mantine-color-text': palette['warm-bone'],
    '--mantine-color-dimmed': palette['taupe-on-walnut'],
    '--mantine-color-default': palette['lifted-walnut'],
    '--mantine-color-default-border': palette['inner-walnut'],
    '--mantine-color-default-hover': palette['inner-walnut'],
    '--mantine-color-placeholder': palette['taupe-on-walnut'],
    '--mantine-color-anchor': palette['instrument-blue-dark'],
    '--mantine-color-error': 'var(--mantine-color-danger-text)',
    '--mantine-color-disabled': palette['inner-walnut'],
    '--mantine-color-disabled-border': palette['inner-walnut'],
    '--mantine-color-disabled-color': palette['taupe-on-walnut'],
    // An input's fill: Input Walnut, a step above the card it sits on, as the app draws it.
    '--ms-input-bg': palette['input-walnut'],
    // A tooltip: Inner Walnut, a step above the rows it sits over, with a
    // warm hairline that holds it off a hovered row of the same tone.
    '--ms-tooltip-bg': palette['inner-walnut'],
    '--ms-tooltip-color': palette['warm-bone'],
    '--ms-tooltip-border': `${palette['taupe-on-walnut']}40`,
    // The dark state colors, the app's own (DESIGN.md 2): an earthy green, a
    // warm amber and a coral red, as a badge's word, as text such as a
    // refusal under an input, and as their 10 to 12 percent tints behind a
    // badge or a notice. Each holds at least 4.8:1 on its tint.
    '--mantine-color-success-light-color': palette['field-green-dark'],
    '--mantine-color-warning-light-color': palette['workshop-amber-dark'],
    '--mantine-color-danger-light-color': palette['vermillion-dark'],
    '--mantine-color-success-text': palette['field-green-dark'],
    '--mantine-color-warning-text': palette['workshop-amber-dark'],
    '--mantine-color-danger-text': palette['vermillion-dark'],
    '--mantine-color-success-light': palette['field-green-dark-tint'],
    '--mantine-color-warning-light': palette['workshop-amber-dark-tint'],
    '--mantine-color-danger-light': palette['vermillion-dark-tint'],
    ...lightVariant('dark'),
  },
});
